import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from "@angular/router";
import {combineLatest, Subscription, take} from "rxjs";
import {filter} from "rxjs/operators";
import {MenuItemComponent} from "../menu-item/menu-item.component";
import {EkDividerComponent} from "@beta-foundations/ui-kit/panel/divider";
import {TranslateModule} from "@ngx-translate/core";
import {EkButtonComponent} from "@beta-foundations/ui-kit/buttons";
import {LogoutMessageComponent} from "../logout-page/logout-message/logout-message.component";
import {DialogService} from "primeng/dynamicdialog";
import {AuthService} from "../../../services/auth.service";
import {UserService} from "../../services/user.service";
import {SettingsService} from "../../services/settings.service";

import {AppSettings} from "../../models/appSettings";
import {User} from "../../models/user";
import {environment} from "../../../../environments/environment";

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, MenuItemComponent, EkDividerComponent, TranslateModule, EkButtonComponent],
    templateUrl: './menu.component.html',
    styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit, OnDestroy {
    @Input() headerTitle: String = '';
    @Input() isOpen: boolean = false;
    @Output() closeMenuRequest = new EventEmitter<void>();

    readonly isTestEnvironment: boolean = environment.envName !== 'prod';

    canViewConversations = false;
    canViewPermissions = false;
    canViewSettings = false;
    canViewOverview = false;
    canViewPrivateChat = false;
    canViewDocuments = false;
    canViewExamples = false;
    canViewTests = false;
    canViewStatistics = false;
    canViewChunks = false;
    canViewIngestionLink = false;

    private dataSubscription: Subscription | undefined;

    constructor(
        private dialogService: DialogService,
        private authService: AuthService,
        private router: Router,
        private userService: UserService,
        private settingsService: SettingsService
    ) {
    }

    ngOnInit(): void {

        const settings$ = this.settingsService.appSettings$;
        const user$ = this.userService.getUser();

        settings$.pipe(take(1)).subscribe(currentSettings => {
            if (!currentSettings) {
                this.settingsService.loadAllSettings().subscribe();
            }
        });

        this.dataSubscription = combineLatest([settings$, user$]).pipe(
            filter(([settings, user]) => !!settings && !!user)
        ).subscribe(([settings, user]) => {
            this.updateMenuVisibility(settings!, user!);
        });
    }

    private updateMenuVisibility(settings: AppSettings[], user: User): void {
        const isUserAdmin = user.group !== 'USER';

        const conversationsSetting = settings.find(s => s.featureName.toLowerCase() === 'conversations');
        this.canViewConversations = conversationsSetting ? conversationsSetting.isEnabled : false;

        const overviewSetting = settings.find(s => s.featureName.toLowerCase() === 'overview');
        this.canViewOverview = overviewSetting ? overviewSetting.isEnabled : false;

        const privateChatSetting = settings.find(s => s.featureName.toLowerCase() === 'private-chat');
        this.canViewPrivateChat = privateChatSetting ? privateChatSetting.isEnabled : false;

        const documentsSetting = settings.find(s => s.featureName.toLowerCase() === 'document');
        this.canViewDocuments = documentsSetting ? documentsSetting.isEnabled : false;

        const examplesSetting = settings.find(s => s.featureName.toLowerCase() === 'examples');
        this.canViewExamples = examplesSetting ? examplesSetting.isEnabled : false;

        const testsSetting = settings.find(s => s.featureName.toLowerCase() === 'tests');
        this.canViewTests = testsSetting ? testsSetting.isEnabled : false;

        const statisticsSetting = settings.find(s => s.featureName.toLowerCase() === 'statistics');
        this.canViewStatistics = statisticsSetting ? statisticsSetting.isEnabled : false;

        const chunksSetting = settings.find(s => s.featureName.toLowerCase() === 'chunk');
        this.canViewChunks = chunksSetting ? chunksSetting.isEnabled : false;

        const ingestionLinkSetting = settings.find(s => s.featureName.toLowerCase() === 'ingestion-link');
        this.canViewIngestionLink = ingestionLinkSetting ? ingestionLinkSetting.isEnabled : false;

        const permissionsSetting = settings.find(s => s.featureName.toLowerCase() === 'permissions');
        const isPermissionsPageActive = permissionsSetting ? permissionsSetting.isEnabled : false;

        this.canViewPermissions = isPermissionsPageActive && isUserAdmin;

        const settingsSetting = settings.find(s => s.featureName.toLowerCase() === 'settings');
        const isSettingsPageActive = settingsSetting ? settingsSetting.isEnabled : false;

        this.canViewSettings = isSettingsPageActive && isUserAdmin;
    }

    ngOnDestroy(): void {
        if (this.dataSubscription) {
            this.dataSubscription.unsubscribe();
        }
    }

    @HostBinding('class.menu-open') get openClass() {
        return this.isOpen;
    }


    onMenuItemClick() {
        this.closeMenuRequest.emit();
    }

    onLogoutClick(): void {
        const confirmRef = this.dialogService.open(LogoutMessageComponent, {
            width: '450px',
            height: '200px',
            modal: true,
            dismissableMask: true,
            style: {right: '7vh', top: '8vh', position: 'fixed', overflow: 'auto'},
            contentStyle: {overflow: 'auto'},
        });

        confirmRef.onClose.subscribe(async (confirmed: boolean) => {
            if (confirmed) {
                try {
                    await this.authService.logout();
                    this.router.navigate(['/login']);
                } catch (error) {
                    console.error("Errore durante il logout:", error);
                }
            }
        });
    }
}
